import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { BsFiletypePdf } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import { TbLoader2 } from "react-icons/tb";
import { saveAs } from "file-saver";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";
import GenericHeader from "../../../Componentes/GenericHeader";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import { formatarData } from "../../../Funcoes/FormatarData";
import { useModal } from "../../../contexts/ModalContext";
import ModalEditInvestigacao from "./ModalEditInvestigacao";
import ModalDeleteInvestigacao from "./ModalDeleteInvestigacao";
import useApi from "../../../hooks/useApi";

const PERMISSAO_BASE = { modulo: "ssma", submodulo: "desvios" };

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover:not(:disabled) {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const IconeLoading = styled(TbLoader2)`
    width: 14px;
    height: 14px;
    animation: ${spin} 1s linear infinite;
`;

const Content = () => {
    const api = useApi();
    const { user } = useContext(AuthContext);
    const { isModalOpen, openModal, closeModal } = useModal();

    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "investigacao-apagar" }),
        [user]
    );

    const [selectedInvestigacao, setSelectedInvestigacao] = useState(null);
    const [investigacaoParaDelete, setInvestigacaoParaDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState([]);
    const [investigacoes, setInvestigacoes] = useState([]);
    const [investigacoesFiltradas, setInvestigacoesFiltradas] = useState([]);

    const fetchData = async () => {
        setLoading(true);

        try {
            const { data } = await api.get("/Painel/API/Desvios/GetInvestigacoes/", {
                withCredentials: true,
            });

            setInvestigacoes(data);
            setInvestigacoesFiltradas(data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const openModalEdit = (obj) => {
        setSelectedInvestigacao(obj);
        openModal("modalEditInvestigacao");
    };

    const closeModalEdit = () => {
        setSelectedInvestigacao(null);
        closeModal();
    };

    const abrirDelete = useCallback(
        (row) => {
            if (!podeApagar) return;
            setInvestigacaoParaDelete(row);
            openModal("investigacaoDelete");
        },
        [podeApagar, openModal]
    );

    const fecharDelete = useCallback(() => {
        setInvestigacaoParaDelete(null);
        closeModal();
    }, [closeModal]);

    const optionObras = useMemo(
        () => [...new Set(investigacoes.map((obj) => obj.centro_custo))].sort(),
        [investigacoes]
    ).filter((cc) => cc && cc.trim() !== "");
    const optionContratos = useMemo(
        () => [...new Set(investigacoes.map((obj) => obj.contrato))].sort(),
        [investigacoes]
    ).filter((c) => c && c.trim() !== "");
    const optionUsuarios = useMemo(
        () => [...new Set(investigacoes.map((obj) => obj.usuario))].sort(),
        [investigacoes]
    ).filter((u) => u && u.trim() !== "");

    const [selectedObras, setSelectedObras] = useState([]);
    const [selectedContratos, setSelectedContratos] = useState([]);
    const [selectedUsuarios, setSelectedUsuarios] = useState([]);
    const [selectedClassificacoes, setSelectedClassificacoes] = useState([]);
    const [selectedResolucoes, setSelectedResolucoes] = useState([]);
    const [dataInicio, setDataInicio] = useState(null);
    const [dataFim, setDataFim] = useState(null);

    useEffect(() => {
        let filtered = investigacoes;

        if (selectedObras.length > 0) {
            filtered = filtered.filter((inv) => selectedObras.includes(inv.centro_custo));
        }
        if (selectedContratos.length > 0) {
            filtered = filtered.filter((inv) => selectedContratos.includes(inv.contrato));
        }
        if (selectedUsuarios.length > 0) {
            filtered = filtered.filter((inv) => selectedUsuarios.includes(inv.usuario));
        }
        if (selectedClassificacoes.length > 0) {
            filtered = filtered.filter((inv) =>
                selectedClassificacoes.includes(inv.classificacao)
            );
        }
        if (selectedResolucoes.length > 0) {
            filtered = filtered.filter((inv) => selectedResolucoes.includes(inv.resolvido));
        }

        if (dataInicio) {
            const inicio = new Date(dataInicio);
            filtered = filtered.filter((inv) => {
                if (!inv.data_ocorrido) return false;
                const dataInv = new Date(inv.data_ocorrido);
                return dataInv >= inicio;
            });
        }
        if (dataFim) {
            const fim = new Date(dataFim);
            filtered = filtered.filter((inv) => {
                if (!inv.data_ocorrido) return false;
                const dataInv = new Date(inv.data_ocorrido);
                return dataInv <= fim;
            });
        }

        setInvestigacoesFiltradas(filtered);
    }, [
        selectedObras,
        selectedContratos,
        selectedUsuarios,
        selectedClassificacoes,
        selectedResolucoes,
        dataInicio,
        dataFim,
        investigacoes,
    ]);

    const generatePDF = useCallback(
        async (item, e) => {
            e.stopPropagation();

            setDownloadingIds((prev) => [...prev, item.id]);

            try {
                const { data } = await api.post(
                    "/Painel/Desvios/Investigacao/assets/php/generate_pdf.php",
                    { id: item.id },
                    {
                        responseType: "blob",
                        withCredentials: true,
                    }
                );

                const blob = new Blob([data], { type: "application/pdf" });
                saveAs(blob, `investigacao_${item.id}.pdf`);
            } catch (error) {
                console.log(error);
            } finally {
                setDownloadingIds((prev) => prev.filter((id) => id !== item.id));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const columns = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessor: "id",
                sortable: true,
                width: "70px",
            },
            {
                id: "id_desvio",
                header: "ID DESVIO",
                accessor: "id_desvio",
                sortable: true,
                width: "100px",
            },
            {
                id: "centro_custo",
                header: "CENTRO CUSTO",
                accessor: "centro_custo",
                sortable: true,
            },
            {
                id: "contrato",
                header: "CONTRATO",
                accessor: "contrato",
                sortable: true,
            },
            {
                id: "usuario",
                header: "PREENCHIDO",
                accessor: (row) => row.usuario || "-",
                sortable: true,
            },
            {
                id: "classificacao",
                header: "CLASSIFICAÇÃO",
                accessor: (row) => row.classificacao || "-",
                sortable: true,
            },
            {
                id: "data_ocorrido",
                header: "DATA",
                accessor: (row) => (row.data_ocorrido ? formatarData(row.data_ocorrido) : "-"),
                sortable: true,
                sortValue: (row) => row.data_ocorrido,
            },
            {
                id: "resolucao",
                header: "RESOLUÇÃO",
                accessor: (row) => (row.resolvido === 1 ? "RESOLVIDO" : "PENDENTE"),
                sortable: true,
                sortValue: (row) => row.resolvido,
            },
            {
                id: "acoes",
                header: "AÇÕES",
                sortable: false,
                width: podeApagar ? "90px" : "70px",
                align: "center",
                render: (_, row) => {
                    const isDownloading = downloadingIds.includes(row.id);
                    return (
                        <AcoesCelula>
                            <BotaoAcao
                                title="Gerar PDF"
                                disabled={isDownloading}
                                onClick={(e) => generatePDF(row, e)}
                            >
                                {isDownloading ? <IconeLoading /> : <BsFiletypePdf size={14} />}
                            </BotaoAcao>
                            {podeApagar && (
                                <BotaoAcao
                                    $danger
                                    title="Remover"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirDelete(row);
                                    }}
                                >
                                    <FiTrash2 size={14} />
                                </BotaoAcao>
                            )}
                        </AcoesCelula>
                    );
                },
            },
        ],
        [podeApagar, abrirDelete, downloadingIds, generatePDF]
    );

    return (
        <>
            <GenericHeader>
                <Filters columns={4}>
                    <MultiselectCheck
                        options={optionObras.map((cc) => ({ label: cc, value: cc }))}
                        selectedOptions={selectedObras}
                        setSelectedOptions={setSelectedObras}
                        placeholder="CENTRO CUSTO"
                    />
                    <MultiselectCheck
                        options={optionContratos.map((c) => ({ label: c, value: c }))}
                        selectedOptions={selectedContratos}
                        setSelectedOptions={setSelectedContratos}
                        placeholder="CONTRATO"
                    />
                    <MultiselectCheck
                        options={optionUsuarios.map((u) => ({ label: u, value: u }))}
                        selectedOptions={selectedUsuarios}
                        setSelectedOptions={setSelectedUsuarios}
                        placeholder="PREENCHIDO"
                    />
                    <MultiselectCheck
                        options={[
                            { label: "AAM", value: "AAM" },
                            { label: "ACA", value: "ACA" },
                            { label: "ASA", value: "ASA" },
                            { label: "INCIDENTE", value: "INCIDENTE" },
                            { label: "NRT", value: "NRT" },
                            { label: "SAA", value: "SAA" },
                        ]}
                        selectedOptions={selectedClassificacoes}
                        setSelectedOptions={setSelectedClassificacoes}
                        placeholder="CLASSIFICAÇÃO"
                    />
                    <MultiselectCheck
                        options={[
                            { value: 0, label: "PENDENTE" },
                            { value: 1, label: "RESOLVIDO" },
                        ]}
                        selectedOptions={selectedResolucoes}
                        setSelectedOptions={setSelectedResolucoes}
                        placeholder="RESOLUÇÃO"
                    />
                    <MultiselectCheck
                        options={[]}
                        selectedOptions={[]}
                        setSelectedOptions={() => {}}
                        placeholder="ACIDENTADO"
                    />
                    <FilterContainer
                        type="date"
                        valor={dataInicio}
                        changeFunc={(valor) => {
                            setDataInicio(valor);
                        }}
                    />
                    <FilterContainer
                        type="date"
                        valor={dataFim}
                        changeFunc={(valor) => {
                            setDataFim(valor);
                        }}
                    />
                </Filters>
            </GenericHeader>
            <TableFlexFill>
                <SortableTable
                    columns={columns}
                    data={investigacoesFiltradas}
                    isLoading={loading}
                    loadingText="Carregando investigações..."
                    emptyText="NENHUMA INVESTIGAÇÃO ENCONTRADA"
                    initialSort={{ key: "id", direction: "desc" }}
                    getRowId={(row) => row.id}
                    onRowClick={openModalEdit}
                    rowCursor="pointer"
                />
            </TableFlexFill>
            {isModalOpen("modalEditInvestigacao") && (
                <ModalEditInvestigacao
                    fecharModal={closeModalEdit}
                    obj={selectedInvestigacao}
                    update={fetchData}
                />
            )}
            {podeApagar && isModalOpen("investigacaoDelete") && investigacaoParaDelete != null && (
                <ModalDeleteInvestigacao
                    investigacao={investigacaoParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={fetchData}
                />
            )}
        </>
    );
};

export default Content;
